import { useState } from 'react';
import { AritoFormV2, BottomActionBarV2, DialogV2, AritoIcon } from '@/components/custom/erp';
import type { GiaBan, GiaBanDetailRow, GiaBanV2, GiaBanV2Input } from '@/types/schemas';
import { formSchema, initialValues, type FormValues } from '../../schemas';
import { useSearchFieldStates } from '../../hooks/useSearchFieldStates';
import useInputTableRow from './InputTableTab/useInputTableRow';
import InputTableTab from './InputTableTab';
import CustomerTab from './CustomerTab';
import { FormMode } from '@/types/form';
import GeneralTab from './GeneralTab';
import BasicForm from './BasicForm';

interface FormDialogProps<TSubmit extends object> {
  mode: FormMode;
  open: boolean;
  onClose: () => void;

  onAddButtonClick?: () => void;
  onEditButtonClick?: () => void;
  onDeleteButtonClick?: () => void;
  onCopyButtonClick?: () => void;
  onWatchButtonClick?: () => void;

  // Parent component's submit handler
  onSubmit?: (data: TSubmit) => void;

  initialData?: GiaBanV2 | GiaBan | Record<string, unknown> | null;
}

function UpdatePriceDialog<TSubmit extends object = GiaBanV2Input>({
  mode,
  open,
  onClose,
  onAddButtonClick,
  onCopyButtonClick,
  onDeleteButtonClick,
  onEditButtonClick,
  onSubmit,
  initialData
}: FormDialogProps<TSubmit>) {
  const {
    vatTu,
    setVatTu,
    donViTinh,
    setDonViTinh,
    ngoaiTe,
    setNgoaiTe,
    khachHang,
    setKhachHang,
    nhomKhachHang1,
    setNhomKhachHang1,
    nhomKhachHang2,
    setNhomKhachHang2,
    nhomKhachHang3,
    setNhomKhachHang3
  } = useSearchFieldStates(initialData as GiaBanV2 | null | undefined);

  const {
    rows,
    selectedRowUuid,
    selectedCell,
    handleRowClick,
    handleCellClick,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    handleCellValueChange
  } = useInputTableRow<GiaBanDetailRow>((initialData as GiaBanV2 | null | undefined)?.gia_ban || []);

  const handleSubmit = (data: FormValues) => {
    const chi_tiet = rows.map(row => ({
      ma_loai_gb: row.ma_loai_gb_data?.uuid || '',
      gia_nt2: row.gia_nt2 || 0
    }));
    onSubmit?.({
      ...data,
      ma_vt: vatTu?.uuid || '',
      dvt: donViTinh?.uuid || '',
      ma_nt: ngoaiTe?.uuid || '',
      ma_kh: khachHang?.uuid || '',
      nh_kh1: nhomKhachHang1?.uuid || '',
      nh_kh2: nhomKhachHang2?.uuid || '',
      nh_kh3: nhomKhachHang3?.uuid || '',
      chi_tiet
    } as TSubmit);
  };
  return (
    <>
      <DialogV2
        open={open}
        onClose={onClose}
        title={mode === 'add' ? 'Mới' : mode === 'edit' ? 'Sửa' : 'Xem'}
        maxWidth='lg'
        disableBackdropClose={true}
        disableEscapeKeyDown={true}
        titleIcon={<AritoIcon icon={281} />}
      >
        <AritoFormV2
          mode={mode}
          hasAritoActionBar={false}
          schema={formSchema}
          onSubmit={handleSubmit}
          initialData={(initialData as Partial<FormValues> | null | undefined) || initialValues}
          className='w-[60vw]'
          headerFields={
            <BasicForm
              mode={mode}
              vatTu={vatTu}
              donViTinh={donViTinh}
              setVatTu={setVatTu}
              setDonViTinh={setDonViTinh}
            />
          }
          tabs={[
            {
              id: '1',
              label: 'Thông tin chung',
              component: <GeneralTab mode={mode} ngoaiTe={ngoaiTe} setNgoaiTe={setNgoaiTe} />
            },
            {
              id: '2',
              label: 'Giá riêng theo khách hàng',
              component: (
                <CustomerTab
                  mode={mode}
                  khachHang={khachHang}
                  nhomKhachHang1={nhomKhachHang1}
                  nhomKhachHang2={nhomKhachHang2}
                  nhomKhachHang3={nhomKhachHang3}
                  setKhachHang={setKhachHang}
                  setNhomKhachHang1={setNhomKhachHang1}
                  setNhomKhachHang2={setNhomKhachHang2}
                  setNhomKhachHang3={setNhomKhachHang3}
                />
              )
            },
            {
              id: '3',
              label: 'Giá chi tiết',
              component: (
                <InputTableTab
                  mode={mode}
                  rows={rows}
                  selectedRowUuid={selectedRowUuid}
                  selectedCell={selectedCell}
                  onRowClick={handleRowClick}
                  onCellClick={handleCellClick}
                  onAddRow={handleAddRow}
                  onDeleteRow={handleDeleteRow}
                  onCopyRow={handleCopyRow}
                  onPasteRow={handlePasteRow}
                  onMoveRow={handleMoveRow}
                  onCellValueChange={handleCellValueChange}
                />
              )
            }
          ]}
          bottomBar={
            <BottomActionBarV2
              mode={mode}
              onAdd={onAddButtonClick}
              onEdit={onEditButtonClick}
              onDelete={onDeleteButtonClick}
              onCopy={onCopyButtonClick}
              onClose={onClose}
            />
          }
        />
      </DialogV2>
    </>
  );
}

export default UpdatePriceDialog;
